import { useEffect, useMemo, useReducer } from 'react';
import { BehaviorSubject, Observable } from 'rxjs';
import { addError } from './Error';
import { Permission } from './GeneralStore';
import {
  fetchAccountCustomizationsApi,
  PostAccountCustomizationRequest,
  postAccountCustomizationApi,
  updateAccountCustomizationApi
} from '../Api/Preferences/AccountCustomizations/accountCustomizationsApi';
import { isFieldVisible } from '../components/Compounds/UpstackDataGrid/columnVisibility';
import { withAccountVisibilityChanges } from '../components/Compounds/UpstackDataGrid/accountDefaultLayout';
import { HomeWidgetDefs } from '../components/Pages/HomePage/HomeWidgetDefs';
import { CasesWidgetDefs } from '../components/Pages/SupportCases/CasesWidgetDefs';
import { InventoryWidgetDefs } from '../components/Pages/Inventory/InventoryWidgetDefs';
import { FormStateStatus } from './BulkForm';
import {
  GridPreferences,
  OrderRequestPreferences,
  UserPreferencesContent,
  WidgetPreferences,
  Widgets,
  getEffectiveAccountId,
  getUserPreferencesSubject,
  gridInitialState,
  hasPermissions
} from './User';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import {
  DefaultInventoryGridColumnDefs,
  inventoryRowAccountId
} from '../components/Pages/Inventory/inventoryGridColumnDefs';
import {
  DefaultProjectGridColumnsDefs,
  getDefaultProjectDetailsGridColumnsDefs
} from '../components/Pages/Projects/projectGridColumnsDefs';
import { projectsRelatedInventoryColumns } from '../components/Pages/Projects/ProjectsRelatedInventoryGridColumnDefs';
import { GroupedGridColDef } from '../components/Compounds/UpstackDataGrid/gridColumnTypes';
import { Account, InventoryAsset } from '../types';
import { ProjectWidgetDefs } from '../components/Pages/Projects/ProjectsWidgetDefs';

export type AccountCustomizationContent = {
  widgets: Widgets;
  inventory_grid: GridPreferences;
  project_grid?: GridPreferences;
  project_details_grid?: GridPreferences;
  related_project_assets_grid?: GridPreferences;
  customizations?: string;
  sub_accounts?: Account[];
  additionalRecipients?: string[];
  comments?: boolean;
  supplier_contacts?: boolean;
  non_recurring_inventory?: boolean;
  order_request?: OrderRequestPreferences;
};

export type AccountCustomizationData = {
  id?: string;
  account_name: string | undefined;
  account_id: string | undefined;
  updated_at: string;
  content: AccountCustomizationContent;
};

export const defaultAccountCustomizationData = (account_id?: string, account_name?: string) => ({
  account_id: account_id || '',
  account_name: account_name || '',
  content: {
    inventory_grid: {
      muiConfig: gridInitialState(DefaultInventoryGridColumnDefs) as GridInitialStatePro,
      disabledFields: ['customerLocationName', 'customerLabelOther', 'demarcationDevice']
    },
    project_grid: {
      muiConfig: gridInitialState(DefaultProjectGridColumnsDefs) as GridInitialStatePro,
      disabledFields: []
    },
    project_details_grid: {
      muiConfig: gridInitialState(getDefaultProjectDetailsGridColumnsDefs()) as GridInitialStatePro,
      disabledFields: []
    },
    related_project_assets_grid: {
      muiConfig: gridInitialState(projectsRelatedInventoryColumns(true) as GroupedGridColDef[]) as GridInitialStatePro,
      disabledFields: []
    },
    widgets: {
      home: [...HomeWidgetDefs],
      cases: [...CasesWidgetDefs],
      inventory: [...InventoryWidgetDefs],
      projects: [...ProjectWidgetDefs]
    },
    sub_accounts: [],
    additionalRecipients: [],
    non_recurring_inventory: true,
    comments: true
  },
  updated_at: ''
});

export type Customizations = GridPreferences | Widgets | OrderRequestPreferences;

export interface AccountCustomizationFormProps extends PostAccountCustomizationRequest {
  dirty: boolean;
  valid: boolean;
  id: string;
  ui_element: React.ReactElement;
  widgets_custom: boolean;
  inventory_grid_custom: boolean;
}

export interface CreateAccountCustomizationState {
  status: FormStateStatus;
  error?: string;
  data?: Partial<AccountCustomizationData>;
  requestBody: PostAccountCustomizationRequest;
}

export const isColumnCustomization = (cust: Customizations): cust is GridPreferences =>
  (cust as GridPreferences)?.muiConfig !== undefined && (cust as GridPreferences).disabledFields !== undefined;

export const isWidgetCustomization = (cust: Customizations): cust is Widgets =>
  (cust as Widgets)?.home !== undefined &&
  (cust as Widgets)?.cases !== undefined &&
  (cust as Widgets)?.inventory !== undefined &&
  (cust as Widgets)?.projects !== undefined;

export const isWidgetPreference = (cust: unknown): cust is WidgetPreferences =>
  (cust as WidgetPreferences).widgetId !== undefined;

export const updateWidgetDef = (widget: WidgetPreferences, updates: WidgetPreferences[]): WidgetPreferences => {
  const matched = updates.find((w) => w.widgetId === widget.widgetId);
  return matched || widget;
};

export const isCustom = (customizations: Customizations, asUser = false, defaultDefs?: Customizations): boolean => {
  if (isColumnCustomization(customizations)) {
    return isGridCustom(customizations, asUser, defaultDefs as GridPreferences);
  }

  if (isWidgetCustomization(customizations)) {
    return isWidgetsCustom(customizations, asUser, defaultDefs as Widgets);
  }

  return false;
};

const isGridCustom = (grid: GridPreferences, asUser = false, defaultDef?: GridPreferences) => {
  if (asUser) {
    const { columnVisibilityModel: visibilityDefault = {}, orderedFields: orderedDefault = [] } =
      defaultDef?.muiConfig.columns || {};
    const { columnVisibilityModel: visibilityCurr = {}, orderedFields: orderedCurr = [] } =
      grid.muiConfig.columns || {};
    const orderCustom = orderedCurr.every((item, idx) => item === orderedDefault[idx]);
    const visibiltyCustom = Object.keys(visibilityCurr).every((key) => visibilityCurr[key] === visibilityDefault[key]);

    return orderCustom || visibiltyCustom;
  }
  return grid.disabledFields.length > 0;
};

const isWidgetsCustom = (widgets: Widgets, asUser = false, defaultDef?: Widgets) => {
  return Object.keys(widgets).reduce((isCustom: boolean, key: string): boolean => {
    if (isCustom) return isCustom;
    const widgetCustomizations = widgets[key as keyof Widgets];
    const defaultWidget = (defaultDef || {})[key as keyof Widgets] || [];
    if (asUser) {
      isCustom = widgetCustomizations.some((cust, idx) => {
        const matchingDefIdx = defaultWidget.findIndex((def) => def.widgetId === cust.widgetId);
        return cust.hide || (idx !== matchingDefIdx && matchingDefIdx !== -1);
      });
    } else {
      isCustom = widgetCustomizations.some((c) => c.disabled);
    }
    return isCustom;
  }, false);
};

const accountCustomizationsSubject = new BehaviorSubject<AccountCustomizationData[]>([]);
const accountCustomizationCreateStateSubject = new BehaviorSubject<CreateAccountCustomizationState | null>(null);
const formValidSubject = new BehaviorSubject<boolean>(false);
const accountCustomizationsLoadingSubject = new BehaviorSubject<boolean>(false);
// The list starts empty, so only this tells "not fetched yet" from "fetched, and this account has none".
const accountCustomizationsLoadedSubject = new BehaviorSubject<boolean>(false);

export const getAccountCustomizationsLoadingSubject = (): BehaviorSubject<boolean> =>
  accountCustomizationsLoadingSubject;

export const getAccountCustomizationsSubject = (): BehaviorSubject<AccountCustomizationData[]> =>
  accountCustomizationsSubject;
export const getAccountCustomizationCreateStateSubject = (): BehaviorSubject<CreateAccountCustomizationState | null> =>
  accountCustomizationCreateStateSubject;
export const clearAccountCustomizationCreateStateSubject = () => accountCustomizationCreateStateSubject.next(null);
export const getFormValidSubject = (): BehaviorSubject<boolean> => formValidSubject;
export const getAccountCustomizationsById = (id: string): AccountCustomizationData | undefined => {
  return accountCustomizationsSubject.getValue().find((c) => c.account_id === id);
};

export const getAcctCustByAccountId = (id: string | undefined): AccountCustomizationData | undefined => {
  const customizations = accountCustomizationsSubject.getValue();
  return customizations.find((c) => c.account_id === id);
};

export const getSubAccounts = (accountId?: string | null): Account[] => {
  if (accountId) {
    const matchedCust = getAcctCustByAccountId(accountId);
    return matchedCust?.content?.sub_accounts || [];
  }

  return [];
};

export interface UseAccountCustomizationsStoreReturn {
  fetchAccountCustomizations: () => Promise<void>;
  createRequest: (requestBody: PostAccountCustomizationRequest) => Promise<{ error: { message: string } } | undefined>;
  clear: () => void;
  setAccountCustomizationsData: (accountCustomizations: AccountCustomizationData[]) => void;
  updateRequest: (
    id: string,
    requestBody: PostAccountCustomizationRequest,
    type?: string
  ) => Promise<{ error: { message: string } } | undefined>;
}

export const useAccountCustomizationsStore = (): UseAccountCustomizationsStoreReturn => {
  const fetchAccountCustomizations = async (): Promise<void> => {
    accountCustomizationsLoadingSubject.next(true);

    const { data, error } = await fetchAccountCustomizationsApi();

    if (data) {
      const formattedCust = data.map((cust) => {
        const content = cust?.content;
        if (!content) return cust;

        const vals = [];

        const existingGrids = content.inventory_grid || [];

        if (isCustom(content.widgets)) vals.push('Widgets');
        if (isCustom(existingGrids)) vals.push('Table Columns');

        cust.content.customizations = vals.join(', ');
        return cust;
      });
      accountCustomizationsSubject.next(formattedCust);
    }
    if (error) addError('account-customization', error.message);

    accountCustomizationsLoadedSubject.next(true);
    accountCustomizationsLoadingSubject.next(false);
  };

  const createRequest = async (requestBody: PostAccountCustomizationRequest) => {
    const { data, error } = await postAccountCustomizationApi(requestBody);
    if (data) {
      accountCustomizationCreateStateSubject.next({
        status: FormStateStatus.SUCCESS,
        data,
        error: undefined,
        requestBody
      });
    }

    if (error) {
      accountCustomizationCreateStateSubject.next({
        status: FormStateStatus.FAILED,
        data: requestBody,
        error: error.message,
        requestBody
      });
      return { error };
    }
  };

  const updateRequest = async (id: string, requestBody: PostAccountCustomizationRequest, type?: string) => {
    const { data, error } = await updateAccountCustomizationApi(id, requestBody, type);
    if (data?.account) {
      accountCustomizationCreateStateSubject.next({
        status: FormStateStatus.SUCCESS,
        data: data.account,
        error: undefined,
        requestBody
      });
    }

    if (error) return { error };
  };

  const setAccountCustomizationsData = (accountCustomizations: AccountCustomizationData[]) =>
    accountCustomizationsSubject.next(accountCustomizations);

  const clear = () => {
    accountCustomizationsSubject.next([]);
    accountCustomizationsLoadedSubject.next(false);
  };

  return {
    fetchAccountCustomizations,
    createRequest,
    clear,
    setAccountCustomizationsData,
    updateRequest
  };
};

export type AccountGridKey = 'project_grid' | 'related_project_assets_grid' | 'project_details_grid' | 'inventory_grid';

// The hooks below read their stores in render, so a new key and the value it names arrive together.
const useRerenderOn = (subject: Observable<unknown>): void => {
  const [, refresh] = useReducer((version: number) => version + 1, 0);

  useEffect(() => {
    const sub = subject.subscribe(refresh);
    return () => sub.unsubscribe();
  }, [subject]);
};

// One fetch brings the whole list: skip it while one is in flight, once the account is in it, or if the user may not read it.
const useAccountCustomizationsFor = (accountId: string | null | undefined): void => {
  const { fetchAccountCustomizations } = useAccountCustomizationsStore();

  useEffect(() => {
    if (
      accountId &&
      !getAcctCustByAccountId(accountId) &&
      !accountCustomizationsLoadingSubject.getValue() &&
      hasPermissions([Permission.VIEW_ACCOUNT_CUSTOMIZATION])
    ) {
      void fetchAccountCustomizations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);
};

// Live account read of a grid's authoritative config, kept in sync with the account customizations store.
// Only internal roles may read account customizations, so it returns undefined for customers — they read
// the backend-delivered base key from their own preferences instead.
export const useAccountGrid = (
  accountId: string | null | undefined,
  prefKey: AccountGridKey
): GridPreferences | undefined => {
  useRerenderOn(getAccountCustomizationsSubject());
  useAccountCustomizationsFor(accountId);

  return getAcctCustByAccountId(accountId ?? undefined)?.content[prefKey] as GridPreferences | undefined;
};

// A grid lays its view out from its first columns, so staff wait for the account list, which this asks for itself.
export const useAccountGridReady = (accountId: string | null | undefined): boolean => {
  useRerenderOn(accountCustomizationsLoadedSubject);
  useAccountCustomizationsFor(accountId);

  return (
    accountCustomizationsLoadedSubject.getValue() ||
    !accountId ||
    !!getAcctCustByAccountId(accountId) ||
    !hasPermissions([Permission.VIEW_ACCOUNT_CUSTOMIZATION])
  );
};

const deliveredGridFor = (content: UserPreferencesContent, baseKey: AccountGridKey): GridPreferences | undefined =>
  content[baseKey as keyof UserPreferencesContent] as GridPreferences | undefined;

// Which account's config a row-bearing surface reads: the account the caller knows the surface belongs
// to (right even when internal staff view another account's records), then the row's own portal id — a
// synced record's account_id is Salesforce's, which matches no customization — then the viewer's account.
export const configAccountIdFor = (
  explicitAccountId: string | null | undefined,
  row?: Partial<InventoryAsset>
): string | null | undefined => explicitAccountId || (row && inventoryRowAccountId(row)) || getEffectiveAccountId();

// A grid's authoritative account config, resolved the way the grids resolve it: staff read the live
// account customization, customers fall back to their backend-delivered base key. Extracted so every
// surface honoring the account's column config (grids and detail cards) shares one resolution.
export const useAccountGridConfig = (
  accountId: string | null | undefined,
  baseKey: AccountGridKey
): GridPreferences | undefined => {
  const accountGrid = useAccountGrid(accountId, baseKey);
  useRerenderOn(getUserPreferencesSubject());

  return accountGrid ?? deliveredGridFor(getUserPreferencesSubject().getValue().content, baseKey);
};

// Stable gridSettings fallback for a grid with no account config. A fresh {} each render would change the
// prop's identity and re-fire UpstackDataGrid's restoreState, reverting a just-saved column order.
export const EMPTY_GRID = {} as GridPreferences;

// A card has no column picker, so one the user turned on in their own grid shows here even if the account starts it hidden.
export const isCardFieldVisible = (
  field: string,
  defaultHidden: boolean | undefined,
  config: GridPreferences | undefined,
  turnedOn?: Record<string, boolean>
): boolean => {
  const columnVisibilityModel = config?.muiConfig?.columns?.columnVisibilityModel;
  if (isFieldVisible(field, defaultHidden, config?.disabledFields, columnVisibilityModel)) return true;

  return turnedOn?.[field] === true && !config?.disabledFields?.includes(field);
};

const columnsTurnedOnIn = (
  content: UserPreferencesContent | undefined,
  baseKey: AccountGridKey,
  accountGrid: GridPreferences | undefined
) => {
  const turnedOn: Record<string, boolean> = {};
  Object.entries(content ?? {}).forEach(([key, grid]) => {
    if (!key.startsWith(`${baseKey}_`) || !grid || typeof grid !== 'object') return;
    const shown = withAccountVisibilityChanges(grid as GridPreferences, accountGrid);
    const model: unknown = shown.muiConfig?.columns?.columnVisibilityModel;
    if (!model || typeof model !== 'object') return;
    Object.entries(model).forEach(([field, visible]) => {
      if (visible === true) turnedOn[field] = true;
    });
  });
  return turnedOn;
};

/**
 * Every column the user turned on in any of their own tabs or saved views of the grid, whichever opened the card,
 * each read as its grid shows it against the account's later changes.
 */
export const useColumnsTurnedOn = (
  baseKey: AccountGridKey,
  accountGrid: GridPreferences | undefined
): Record<string, boolean> => {
  useRerenderOn(getUserPreferencesSubject());
  const content = getUserPreferencesSubject().getValue().content;

  return useMemo(() => columnsTurnedOnIn(content, baseKey, accountGrid), [content, baseKey, accountGrid]);
};
